/**
 * `buildTree` — fold the API's **flat** `TaskNodeWithAssignments[]` into a
 * nested tree, ordered by `order_index`. Pure + dependency-free.
 *
 * Issue #759 (Phase 2). The API (`GET …/tasks/nodes`) returns an adjacency
 * list (each node carries `parent_id`; `null` ⇒ root). Per §A3 the tree
 * shape is an adjacency list; the SPA reconstructs the nesting client-side.
 *
 * This is the one place flat→nested happens — both the canvas and the
 * outline rail consume the result, so the structure is computed once and
 * shared (the clean abstraction boundary the founder asks for in §A7).
 *
 * Roll-ups are computed here too (display-only, about the WORK, never a
 * person — §A6 / §A9): each node carries `doneDescendants` /
 * `totalDescendants` so a parent can render "2 of 5 done" without the view
 * re-walking the tree.
 */

import type { TaskState } from "./state";

/**
 * Round-4 ⑥ (#1043) — node kinds.
 *
 * `task` is the pre-existing semantic: a full participant in the tree, the
 * rollups, and the outline rail.
 *
 * `link_block` is the new OFF-TREE freeform annotation: it has no parent_id
 * (and never gets one), is not included in any outcome rollup, and does not
 * appear in the outline rail. The canvas paints it like a card but treats it
 * separately from the tree.
 */
export type TaskNodeKind = "task" | "link_block";

/** A node as it arrives from the API (the relevant subset). */
export interface FlatTaskNode {
  id: string;
  parent_id?: string | null;
  title: string;
  description?: string | null;
  state: TaskState;
  order_index: number;
  estimate_minutes?: number | null;
  target_date?: string | null;
  /** #2631 - start time (ISO datetime), minutes and place of a duty. */
  starts_at?: string | null;
  duration_minutes?: number | null;
  place?: string | null;
  needs_handoff_at?: string | null;
  /**
   * Latest `task_notes.created_at` for this node (ISO datetime), or null when
   * the node has no notes (#1043, item 8). DERIVED on the backend read; the
   * card uses it for a calm "🗒 <relative>" recency cue.
   */
  last_note_at?: string | null;
  /**
   * Round-4 ⑥ (#1043) — pinned canvas position (px, plane-coord). Both NULL ⇒
   * the node flows via the tidy-tree; both set ⇒ the lead pinned it via
   * drag-to-pin and the canvas paints it at this coordinate.
   */
  layout_x?: number | null;
  layout_y?: number | null;
  /**
   * Round-4 ⑥ (#1043) — the node kind. Defaults to `task` when absent so a
   * pre-migration API payload (or a unit-test fixture that omits it) builds
   * the same tree as before.
   */
  kind?: TaskNodeKind;
  assignments: { membership_id: string }[];
}

/** A node after nesting, with its children + computed roll-up. */
export interface TreeNode {
  id: string;
  parentId: string | null;
  title: string;
  description: string | null;
  state: TaskState;
  orderIndex: number;
  /**
   * Optional forward estimate in minutes (display-only, §A1.2 — estimates are
   * a demoted footnote). There is no write-path yet (deferred to the gated
   * `task_estimates` component), so this is read-only on the surface.
   */
  estimateMinutes: number | null;
  /** Optional target date (ISO string), display-only — same deferral as the estimate. */
  targetDate: string | null;
  /** #2631 - start time (ISO datetime), length in minutes and place. */
  startsAt?: string | null;
  durationMinutes?: number | null;
  place?: string | null;
  needsHandoffAt?: string | null;
  /**
   * Latest note timestamp (ISO datetime) for this node, or null when it has no
   * notes (#1043, item 8). A DERIVED recency cue the card renders as a calm
   * "🗒 <relative>"; about the WORK's collaboration thread, never a person (§A9).
   */
  lastNoteAt: string | null;
  /**
   * Round-4 ⑥ (#1043) — pinned canvas position. Both null ⇒ the node flows
   * via the tidy-tree; both set ⇒ the lead pinned it and the canvas paints
   * it at this plane coordinate (the tidy tree still runs but the result is
   * overridden by the layout pass).
   */
  layoutX: number | null;
  layoutY: number | null;
  /** Round-4 ⑥ (#1043) — the node kind. */
  kind: TaskNodeKind;
  /** Membership ids assigned to THIS node (order preserved). */
  assigneeIds: string[];
  /** Direct children, ordered by `order_index` then `title`. */
  children: TreeNode[];
  /** Depth from a root (root = 0). */
  depth: number;
  /** Count of descendant nodes (excludes self) in `done` state. */
  doneDescendants: number;
  /** Count of descendant nodes (excludes self), any state. */
  totalDescendants: number;
}

/**
 * Stable child ordering: `order_index` ascending, ties broken by title
 * then id so layout is deterministic across renders (and tests).
 */
function compareSiblings(a: FlatTaskNode, b: FlatTaskNode): number {
  if (a.order_index !== b.order_index) return a.order_index - b.order_index;
  if (a.title !== b.title) return a.title.localeCompare(b.title);
  return a.id.localeCompare(b.id);
}

/**
 * Partition a flat node list by `kind` so the tree builder only sees task
 * nodes and the canvas can render `link_block`s as a separate freeform
 * overlay (round-4 ⑥, #1043). Pure + dependency-free.
 *
 * A `link_block` is an OFF-TREE freeform annotation: it never participates in
 * the tidy-tree, never rolls up into a parent's `n of m done`, and never
 * appears in the outline rail. Routing it through `buildTree` would either
 * crash the rollup arithmetic (no `state` semantics for a "block") or, worse,
 * leak it into the outline as a phantom outcome. So we split it out cleanly
 * at the front of the pipeline.
 *
 * An absent `kind` (pre-migration payloads / fixtures) defaults to `task` —
 * the partition matches the unmodified pre-⑥ tree shape exactly.
 */
export function partitionByKind(flat: readonly FlatTaskNode[]): {
  tasks: FlatTaskNode[];
  linkBlocks: FlatTaskNode[];
} {
  const tasks: FlatTaskNode[] = [];
  const linkBlocks: FlatTaskNode[] = [];
  for (const node of flat) {
    if (node.kind === "link_block") linkBlocks.push(node);
    else tasks.push(node);
  }
  return { tasks, linkBlocks };
}

/**
 * Fold a flat node list into root trees. Nodes whose `parent_id` points at
 * a node not present in the list (e.g. an archived/hidden parent) are
 * treated as roots so nothing silently vanishes from the canvas.
 *
 * Round-4 ⑥ (#1043): `link_block` nodes are EXCLUDED from the tree (they are
 * off-tree freeform annotations; the canvas renders them via a separate pass).
 * The exclusion runs at the front so the tree shape is byte-identical to the
 * pre-⑥ behaviour for a payload of pure `task` nodes.
 *
 * @returns the forest (top-level roots, ordered). Most teams have exactly
 *   one root (the output), but the model permits several.
 */
export function buildTree(flat: readonly FlatTaskNode[]): TreeNode[] {
  const { tasks } = partitionByKind(flat);
  const byId = new Map<string, FlatTaskNode>(tasks.map((n) => [n.id, n]));
  const childrenOf = new Map<string | null, FlatTaskNode[]>();

  for (const node of tasks) {
    // A parent_id that doesn't resolve in the TASK list ⇒ treat node as a
    // root. (A task whose parent is a link_block can never exist — link_blocks
    // never become parents at the service layer.)
    const key = node.parent_id && byId.has(node.parent_id) ? node.parent_id : null;
    const bucket = childrenOf.get(key);
    if (bucket) bucket.push(node);
    else childrenOf.set(key, [node]);
  }

  const build = (flatNode: FlatTaskNode, depth: number): TreeNode => {
    const kids = (childrenOf.get(flatNode.id) ?? [])
      .slice()
      .sort(compareSiblings)
      .map((child) => build(child, depth + 1));

    const totalDescendants = kids.reduce((sum, k) => sum + 1 + k.totalDescendants, 0);
    const doneDescendants = kids.reduce(
      (sum, k) => sum + (k.state === "done" ? 1 : 0) + k.doneDescendants,
      0,
    );

    return {
      id: flatNode.id,
      parentId: flatNode.parent_id ?? null,
      title: flatNode.title,
      description: flatNode.description ?? null,
      state: flatNode.state,
      orderIndex: flatNode.order_index,
      estimateMinutes: flatNode.estimate_minutes ?? null,
      targetDate: flatNode.target_date ?? null,
      startsAt: flatNode.starts_at ?? null,
      durationMinutes: flatNode.duration_minutes ?? null,
      place: flatNode.place ?? null,
      needsHandoffAt: flatNode.needs_handoff_at ?? null,
      lastNoteAt: flatNode.last_note_at ?? null,
      layoutX: flatNode.layout_x ?? null,
      layoutY: flatNode.layout_y ?? null,
      kind: flatNode.kind ?? "task",
      assigneeIds: flatNode.assignments.map((a) => a.membership_id),
      children: kids,
      depth,
      doneDescendants,
      totalDescendants,
    };
  };

  return (childrenOf.get(null) ?? [])
    .slice()
    .sort(compareSiblings)
    .map((root) => build(root, 0));
}

/**
 * Build the off-tree `link_block` nodes as standalone `TreeNode`s (no
 * children, depth 0) so the canvas can render them with the same card
 * mechanics as tasks (round-4 ⑥, #1043). Each link_block carries its
 * `layoutX`/`layoutY` so the layout-override pass can place it.
 */
export function buildLinkBlocks(flat: readonly FlatTaskNode[]): TreeNode[] {
  const { linkBlocks } = partitionByKind(flat);
  return linkBlocks
    .slice()
    .sort(compareSiblings)
    .map(
      (node): TreeNode => ({
        id: node.id,
        parentId: null,
        title: node.title,
        description: node.description ?? null,
        state: node.state,
        orderIndex: node.order_index,
        estimateMinutes: node.estimate_minutes ?? null,
        targetDate: node.target_date ?? null,
        startsAt: node.starts_at ?? null,
        durationMinutes: node.duration_minutes ?? null,
        place: node.place ?? null,
        lastNoteAt: node.last_note_at ?? null,
        layoutX: node.layout_x ?? null,
        layoutY: node.layout_y ?? null,
        kind: "link_block",
        assigneeIds: node.assignments.map((a) => a.membership_id),
        children: [],
        depth: 0,
        doneDescendants: 0,
        totalDescendants: 0,
      }),
    );
}

/** Flatten a forest back to a depth-first ordered list (rail / iteration). */
export function flattenTree(roots: readonly TreeNode[]): TreeNode[] {
  const out: TreeNode[] = [];
  const walk = (node: TreeNode) => {
    out.push(node);
    for (const child of node.children) walk(child);
  };
  for (const root of roots) walk(root);
  return out;
}
